import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Upload, Trash2, Check, Loader2, ImageIcon, FolderOpen } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { IconFilePickerDialog } from '../components/IconFilePickerDialog';

interface ArcheSummary {
  archId: string;
  name: string;
  simpleDescription: string;
  imageRef: string | null;
}

type UploadStatus = 'idle' | 'getting-url' | 'uploading' | 'confirming' | 'done' | 'error';

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];
const ENV_LABELS: Record<string, string> = {
  local: 'Local',
  staging: 'Staging',
  prod: 'Production',
};

export function ArcheIconManager() {
  const [arches, setArches] = useState<ArcheSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedArchId, setSelectedArchId] = useState<string | null>(null);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>('idle');
  const [statusMessage, setStatusMessage] = useState('');
  const [preview, setPreview] = useState<string | null>(null);
  const [availableEnvs, setAvailableEnvs] = useState<string[]>([]);
  const [selectedEnvs, setSelectedEnvs] = useState<string[]>([]);
  const [envError, setEnvError] = useState<string | null>(null);
  const [fiasPickerOpen, setFiasPickerOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchArches = useCallback(async () => {
    try {
      setLoading(true);
      const result = await api.listArches({ limit: 200 });
      const mapped = (result.arches as ArcheSummary[]).map((a) => ({
        archId: a.archId,
        name: a.name,
        simpleDescription: a.simpleDescription,
        imageRef: a.imageRef,
      }));
      mapped.sort((a, b) => a.name.localeCompare(b.name));
      setArches(mapped);
    } catch (err) {
      console.error('Failed to fetch arches', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEnvironments = useCallback(async () => {
    try {
      setEnvError(null);
      const { environments } = await api.adminGetArcheIconEnvironments();
      setAvailableEnvs(environments);
      setSelectedEnvs(environments);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.error('Failed to fetch icon environments', err);
      setEnvError(message);
    }
  }, []);

  useEffect(() => {
    fetchArches();
    fetchEnvironments();
  }, [fetchArches, fetchEnvironments]);

  const selectedArche = arches.find((a) => a.archId === selectedArchId) ?? null;

  const toggleEnv = (env: string) => {
    setSelectedEnvs((prev) =>
      prev.includes(env) ? prev.filter((e) => e !== env) : [...prev, env],
    );
  };

  const isUploading =
    uploadStatus === 'getting-url' || uploadStatus === 'uploading' || uploadStatus === 'confirming';

  const processIconFile = useCallback(
    async (file: File) => {
      if (!selectedArchId) return;

      if (!ALLOWED_TYPES.includes(file.type)) {
        setUploadStatus('error');
        setStatusMessage('Invalid file type. Allowed: PNG, JPEG, WebP, SVG');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setUploadStatus('error');
        setStatusMessage('File too large. Maximum size: 5MB');
        return;
      }

      // Validate image dimensions (1024x1024 required, skip for SVG)
      if (file.type !== 'image/svg+xml') {
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);
        try {
          const dimensions = await new Promise<{ width: number; height: number }>(
            (resolve, reject) => {
              img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
              img.onerror = () => reject(new Error('Failed to read image'));
              img.src = objectUrl;
            },
          );

          if (dimensions.width !== 1024 || dimensions.height !== 1024) {
            setUploadStatus('error');
            setStatusMessage(
              `Image must be 1024×1024 pixels. Got ${dimensions.width}×${dimensions.height}.`,
            );
            return;
          }
        } finally {
          URL.revokeObjectURL(objectUrl);
        }
      }

      if (selectedEnvs.length === 0) {
        setUploadStatus('error');
        setStatusMessage('Select at least one environment to upload to');
        return;
      }

      // Show preview
      const reader = new FileReader();
      reader.onload = () => setPreview(reader.result as string);
      reader.readAsDataURL(file);

      try {
        // Step 1: Get presigned URLs for selected environments
        setUploadStatus('getting-url');
        setStatusMessage(`Getting upload URLs for ${selectedEnvs.join(', ')}...`);
        const { uploads } = await api.adminGetArcheIconUploadUrls(
          selectedArchId,
          file.type,
          file.size,
          selectedEnvs,
        );

        // Step 2: Upload to all selected environments in parallel
        setUploadStatus('uploading');
        const envList = Object.keys(uploads);
        setStatusMessage(`Uploading to ${envList.map((e) => ENV_LABELS[e] || e).join(' & ')}...`);

        const uploadResults = await Promise.allSettled(
          envList.map(async (env) => {
            const res = await fetch(uploads[env].uploadUrl, {
              method: 'PUT',
              headers: { 'Content-Type': file.type },
              body: file,
            });
            if (!res.ok) throw new Error(`S3 upload failed for ${env}: ${res.status}`);
            return env;
          }),
        );

        const failedUploads = uploadResults
          .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
          .map((r) => r.reason?.message || 'Unknown error');

        if (failedUploads.length > 0) {
          throw new Error(failedUploads.join('; '));
        }

        // Step 3: Confirm upload (use s3Key from any env — they're the same)
        const s3Key = Object.values(uploads)[0].s3Key;
        setUploadStatus('confirming');
        setStatusMessage('Confirming upload...');
        const { arche } = await api.adminConfirmArcheIcon(selectedArchId, s3Key, selectedEnvs);

        // Update local state
        setArches((prev) =>
          prev.map((a) => (a.archId === selectedArchId ? { ...a, imageRef: arche.imageRef } : a)),
        );

        setUploadStatus('done');
        const envNames = selectedEnvs.map((e) => ENV_LABELS[e] || e).join(' & ');
        setStatusMessage(`Icon updated for ${arche.name} (${envNames})`);
      } catch (err) {
        setUploadStatus('error');
        setStatusMessage(err instanceof Error ? err.message : 'Upload failed');
        console.error('Icon upload failed', err);
      }
    },
    [selectedArchId, selectedEnvs],
  );

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await processIconFile(file);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFiasFilePicked = async (file: File) => {
    await processIconFile(file);
  };

  const handleDelete = async () => {
    if (!selectedArchId) return;

    try {
      setUploadStatus('confirming');
      setStatusMessage('Removing icon...');
      await api.adminDeleteArcheIcon(
        selectedArchId,
        selectedEnvs.length > 0 ? selectedEnvs : undefined,
      );

      setArches((prev) =>
        prev.map((a) => (a.archId === selectedArchId ? { ...a, imageRef: null } : a)),
      );
      setPreview(null);
      setUploadStatus('done');
      setStatusMessage('Icon removed');
    } catch (err) {
      setUploadStatus('error');
      setStatusMessage(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  const resetUpload = () => {
    setUploadStatus('idle');
    setStatusMessage('');
    setPreview(null);
  };

  return (
    <div className="max-w-4xl mx-auto p-8">
      <div className="mb-6">
        <Link
          to="/dev-console"
          className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground text-sm mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Dev Console
        </Link>
        <h1 className="text-3xl font-bold text-foreground">Arche Icon Manager</h1>
        <p className="mt-2 text-muted-foreground">
          Upload and manage icons for arches. Icons are stored in S3 and served via CloudFront CDN.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Arche selector */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Select an Arche</CardTitle>
            <CardDescription>Choose which arche to update the icon for</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto space-y-1">
                {arches.map((arche) => (
                  <button
                    key={arche.archId}
                    onClick={() => {
                      setSelectedArchId(arche.archId);
                      resetUpload();
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                      selectedArchId === arche.archId
                        ? 'bg-primary/10 text-primary'
                        : 'hover:bg-muted'
                    }`}
                  >
                    <div className="w-8 h-8 rounded-md flex-shrink-0 overflow-hidden">
                      {arche.imageRef ? (
                        <img src={arche.imageRef} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-muted flex items-center justify-center">
                          <ImageIcon className="h-4 w-4 text-muted-foreground" />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{arche.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{arche.archId}</div>
                    </div>
                    {arche.imageRef && (
                      <Check className="h-4 w-4 text-green-500 ml-auto flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upload panel */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {selectedArche ? `Icon: ${selectedArche.name}` : 'Upload Icon'}
            </CardTitle>
            <CardDescription>
              {selectedArche
                ? 'Upload a new icon image (PNG, JPEG, WebP, or SVG, max 5MB)'
                : 'Select an arche from the list first'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!selectedArche ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                <ImageIcon className="h-12 w-12" />
              </div>
            ) : (
              <div className="space-y-6">
                {/* Current icon preview */}
                <div>
                  <Label className="text-sm text-muted-foreground">Current Icon</Label>
                  <div className="mt-2 flex items-center gap-4">
                    <div className="w-20 h-20 rounded-xl overflow-hidden border border-border">
                      {preview || selectedArche.imageRef ? (
                        <img
                          src={preview || selectedArche.imageRef || ''}
                          alt={selectedArche.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-b from-primary/80 to-primary flex items-center justify-center">
                          <span className="text-2xl font-bold text-primary-foreground">
                            {selectedArche.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                    </div>
                    {selectedArche.imageRef && (
                      <div className="text-xs text-muted-foreground break-all max-w-48">
                        {selectedArche.imageRef}
                      </div>
                    )}
                  </div>
                </div>

                {/* Environment checkboxes */}
                {availableEnvs.length > 0 && (
                  <div>
                    <Label className="text-sm text-muted-foreground">Upload to</Label>
                    <div className="mt-2 flex gap-4">
                      {availableEnvs.map((env) => (
                        <label
                          key={env}
                          className="flex items-center gap-2 cursor-pointer select-none"
                        >
                          <input
                            type="checkbox"
                            checked={selectedEnvs.includes(env)}
                            onChange={() => toggleEnv(env)}
                            disabled={isUploading}
                            className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                          />
                          <span className="text-sm font-medium">{ENV_LABELS[env] || env}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {availableEnvs.length === 0 && envError && (
                  <div className="text-sm text-amber-600 dark:text-amber-400 bg-amber-500/10 px-3 py-2 rounded-md">
                    Failed to load environments: {envError}
                  </div>
                )}

                {availableEnvs.length === 0 && !envError && (
                  <div className="text-sm text-amber-600 dark:text-amber-400 bg-amber-500/10 px-3 py-2 rounded-md">
                    No environments configured. Check that ARCHE_ICONS_BUCKET and
                    ARCHE_ICONS_CDN_URL are set on the server.
                  </div>
                )}

                {/* Upload controls */}
                <div className="space-y-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ALLOWED_TYPES.join(',')}
                    onChange={handleFileSelect}
                    className="hidden"
                    id="icon-upload"
                  />
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <Button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading || selectedEnvs.length === 0}
                        className="flex-1"
                      >
                        {isUploading ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Upload className="h-4 w-4 mr-2" />
                        )}
                        Upload from System
                      </Button>
                      {selectedArche.imageRef && (
                        <Button variant="destructive" onClick={handleDelete} disabled={isUploading}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <Button
                      variant="secondary"
                      onClick={() => setFiasPickerOpen(true)}
                      disabled={isUploading || selectedEnvs.length === 0}
                      className="w-full"
                    >
                      <FolderOpen className="h-4 w-4 mr-2" />
                      Choose from Fias Files
                    </Button>
                  </div>
                </div>

                {/* Status message */}
                {statusMessage && (
                  <div
                    className={`text-sm px-3 py-2 rounded-md ${
                      uploadStatus === 'error'
                        ? 'bg-destructive/10 text-destructive'
                        : uploadStatus === 'done'
                          ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                          : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {statusMessage}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <IconFilePickerDialog
        open={fiasPickerOpen}
        onOpenChange={setFiasPickerOpen}
        onFileSelected={handleFiasFilePicked}
      />
    </div>
  );
}

export default ArcheIconManager;
